import { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Edit, Trash2, Star, Save } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL 
  ? `${process.env.REACT_APP_BACKEND_URL}/api` 
  : '/api';

// List View
export function AdminTestimonialsList() {
  const { getAuthHeader } = useAuth();
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    try {
      const response = await fetch(`${API}/testimonials`);
      if (response.ok) {
        const data = await response.json();
        setTestimonials(data);
      }
    } catch (error) {
      console.error('Error:', error);
    }
    setLoading(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce témoignage ?')) return;
    
    try {
      const response = await fetch(`${API}/admin/testimonials/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': getAuthHeader() }
      });
      if (response.ok) {
        toast.success('Témoignage supprimé');
        fetchTestimonials();
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const renderStars = (rating) => {
    return '★'.repeat(rating) + '☆'.repeat(5 - rating);
  };

  return (
    <div className="min-h-screen bg-cream">
      <header className="bg-forest text-white py-4 px-6">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin/dashboard" className="hover:text-sand">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <h1 className="font-heading text-xl font-bold">Témoignages</h1>
          </div>
          <Link to="/admin/testimonials/new">
            <Button className="bg-autumn hover:bg-autumn-dark">
              <Plus className="w-4 h-4 mr-2" />
              Nouveau témoignage
            </Button>
          </Link>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8">
        {loading ? (
          <p className="text-center text-forest/60">Chargement...</p>
        ) : testimonials.length === 0 ? (
          <div className="text-center py-12">
            <Star className="w-12 h-12 text-forest/30 mx-auto mb-4" />
            <p className="font-body text-forest/60">Aucun témoignage enregistré</p>
            <Link to="/admin/testimonials/new" className="text-autumn hover:underline mt-2 inline-block">
              Ajouter le premier témoignage
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {testimonials.map((testimonial) => (
              <div key={testimonial.id} className="bg-white rounded-xl p-4 shadow-soft">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="font-heading font-semibold text-forest">{testimonial.author_name}</span>
                      <span className="text-amber-500">{renderStars(testimonial.rating)}</span>
                      {testimonial.is_featured && (
                        <span className="bg-autumn/10 text-autumn text-xs px-2 py-1 rounded-full">Mis en avant</span>
                      )}
                    </div>
                    <p className="font-body text-sm text-forest/70 line-clamp-2">{testimonial.content}</p>
                    <p className="font-body text-xs text-forest/50 mt-1">{testimonial.date}</p>
                  </div>
                  <div className="flex gap-2">
                    <Link to={`/admin/testimonials/edit/${testimonial.id}`}>
                      <Button variant="outline" size="sm">
                        <Edit className="w-4 h-4" />
                      </Button>
                    </Link>
                    <Button variant="outline" size="sm" onClick={() => handleDelete(testimonial.id)} className="text-red-500 hover:bg-red-50">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

// Form View
export function AdminTestimonialsForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const { getAuthHeader } = useAuth();
  
  const [formData, setFormData] = useState({
    author_name: '',
    rating: 5,
    content: '',
    source: 'google',
    date: new Date().toISOString().split('T')[0],
    is_featured: true
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isEdit) {
      fetchTestimonial();
    }
  }, [id]);

  const fetchTestimonial = async () => {
    try {
      const response = await fetch(`${API}/testimonials`);
      if (response.ok) {
        const data = await response.json();
        const testimonial = data.find(t => t.id === id);
        if (testimonial) {
          setFormData(testimonial);
        }
      }
    } catch (error) {
      toast.error('Erreur lors du chargement');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const url = isEdit ? `${API}/admin/testimonials/${id}` : `${API}/admin/testimonials`;
      const method = isEdit ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': getAuthHeader(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success(isEdit ? 'Témoignage mis à jour' : 'Témoignage ajouté');
        navigate('/admin/testimonials');
      } else {
        toast.error('Erreur lors de la sauvegarde');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-cream">
      <header className="bg-forest text-white py-4 px-6">
        <div className="max-w-3xl mx-auto flex items-center gap-4">
          <Link to="/admin/testimonials" className="hover:text-sand">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <h1 className="font-heading text-xl font-bold">
            {isEdit ? 'Modifier le témoignage' : 'Nouveau témoignage'}
          </h1>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-8">
        <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-6 shadow-soft space-y-6">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="font-body font-semibold text-forest">Nom de l'auteur *</Label>
              <Input
                value={formData.author_name}
                onChange={(e) => setFormData({...formData, author_name: e.target.value})}
                className="mt-1"
                placeholder="Ex: Sophie Martin"
                required
              />
            </div>
            <div>
              <Label className="font-body font-semibold text-forest">Date</Label>
              <Input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({...formData, date: e.target.value})}
                className="mt-1"
              />
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="font-body font-semibold text-forest">Note (1-5) *</Label>
              <Input
                type="number"
                min="1"
                max="5"
                value={formData.rating}
                onChange={(e) => setFormData({...formData, rating: parseInt(e.target.value)})}
                className="mt-1"
                required
              />
              <p className="text-amber-500 mt-1">{'★'.repeat(formData.rating)}{'☆'.repeat(5 - formData.rating)}</p>
            </div>
            <div>
              <Label className="font-body font-semibold text-forest">Source</Label>
              <Input
                value={formData.source}
                onChange={(e) => setFormData({...formData, source: e.target.value})}
                className="mt-1"
                placeholder="google"
              />
            </div>
          </div>

          <div>
            <Label className="font-body font-semibold text-forest">Contenu du témoignage *</Label>
            <Textarea
              value={formData.content}
              onChange={(e) => setFormData({...formData, content: e.target.value})}
              className="mt-1 min-h-[150px]"
              placeholder="Le témoignage de l'adoptant..."
              required
            />
          </div>

          <div className="flex items-center gap-2">
            <Checkbox
              id="featured"
              checked={formData.is_featured}
              onCheckedChange={(checked) => setFormData({...formData, is_featured: checked})}
            />
            <Label htmlFor="featured" className="font-body text-forest cursor-pointer">
              Mettre en avant sur la page d'accueil
            </Label>
          </div>

          <div className="flex gap-4 pt-4">
            <Button type="submit" disabled={loading} className="bg-forest hover:bg-forest-dark">
              <Save className="w-4 h-4 mr-2" />
              {loading ? 'Enregistrement...' : 'Enregistrer'}
            </Button>
            <Link to="/admin/testimonials">
              <Button type="button" variant="outline">Annuler</Button>
            </Link>
          </div>
        </form>
      </main>
    </div>
  );
}
